import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { TextField } from '@mui/material';
import { profileUpdationSchema } from '../../context/UserProfileContext/validation/schema-validator';
import {
  Alert,
  Form,
  FieldGroup,
  FieldRow,
  Label,
  Input,
  PhoneInput,
  Select,
  SectionTitle,
  Button,
  ContactFormHeaderScroll,
} from './styles/contact-form-styles';
import { ContactFormHeader } from './FormHeader';

// Funções de máscara para telefone e celular
const formatPhoneNumber = value => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  } else {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
  }
};

const formatCellPhone = value => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 3) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  } else if (numbers.length <= 7) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 3)} ${numbers.slice(3)}`;
  } else {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 3)} ${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }
};

const DEFAULT_ERROR_MESSAGE = 'Não foi possível atualizar os dados.';
const PHONE_ERROR_MESSAGE = 'O número de celular já está em uso.';
const COMPANY_PHONE_ERROR_MESSAGE = 'O número de telefone comercial já está em uso.';

export const ContactForm = ({ initialValues, onSubmit, status }) => {
  const {
    values: formValues,
    errors: formErrors,
    handleChange: onFieldChange,
    resetForm,
    touched,
    setValues,
    handleBlur,
    handleSubmit,
  } = useFormik({
    initialValues: {
      companyPhone: initialValues.companyPhone || '',
      phoneNumber: initialValues.phoneNumber || '',
    },
    onSubmit,
    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: profileUpdationSchema,
  });

  // Atualizar valores quando initialValues mudar
  useEffect(() => {
    setValues({
      companyPhone: initialValues.companyPhone || '',
      phoneNumber: initialValues.phoneNumber || '',
    });
  }, [initialValues, setValues]);

  // Função para lidar com mudanças nos campos de telefone
  const handlePhoneChange = (fieldName, value) => {
    const formattedValue =
      fieldName === 'phoneNumber' ? formatCellPhone(value) : formatPhoneNumber(value);

    onFieldChange({
      target: {
        name: fieldName,
        value: formattedValue,
      },
    });
  };

  const handleSubmitForm = form => {
    handleSubmit(form);
  };

  return (
    <Form onSubmit={handleSubmitForm}>
      <ContactFormHeader />
      <ContactFormHeaderScroll>
        <FieldGroup>
          <TextField
            id="dealershipName"
            label="Nome da loja"
            value={initialValues.dealershipName}
            disabled
            placeholder="SENHOR AUTOMÓVEIS"
            variant="outlined"
            fullWidth
          />
        </FieldGroup>

        <FieldGroup>
          <TextField
            id="cnpj"
            label="CNPJ"
            value={initialValues.cnpj}
            disabled
            placeholder="33.216.200-0001/33"
            variant="outlined"
            fullWidth
          />
        </FieldGroup>

        <FieldGroup>
          <TextField
            id="email"
            label="E-mail"
            type="email"
            value={initialValues.email}
            disabled
            placeholder="senhor.auto@gmail.com"
            variant="outlined"
            fullWidth
          />
        </FieldGroup>
        <FieldRow>
          <FieldGroup>
            <TextField
              id="companyPhone"
              name="companyPhone"
              label="Telefone"
              value={formValues.companyPhone}
              onChange={e => handlePhoneChange('companyPhone', e.target.value)}
              defaultValue={initialValues.companyPhone}
              type="tel"
              onBlur={handleBlur}
              placeholder="(11) 0000-0000"
              variant="outlined"
              fullWidth
              error={!!formErrors.companyPhone || status.companyPhoneError} // Exibe erro se houver
              helperText={formErrors.companyPhone || (status.companyPhoneError && status.companyPhoneErrorMessage)}
              inputProps={{ maxLength: 14 }}
            />
          </FieldGroup>

          <FieldGroup>
            <TextField
              id="phoneNumber"
              name="phoneNumber"
              label="Celular"
              value={formValues.phoneNumber}
              onChange={e => handlePhoneChange('phoneNumber', e.target.value)}
              type="tel"
              onBlur={handleBlur}
              placeholder="(11) 9 0000-0000"
              variant="outlined"
              fullWidth
              error={!!formErrors.phoneNumber || status.phoneNumberError} // Exibe erro se houver
              helperText={formErrors.phoneNumber || (status.phoneNumberError && status.phoneNumberErrorMessage)}
              inputProps={{ maxLength: 16 }}
            />
          </FieldGroup>
        </FieldRow>

        {status.success && <Alert severity="success">Dados atualizados!</Alert>}
        {status.error && <Alert severity="error">{status.phoneNumberErrorMessage || status.companyPhoneErrorMessage || PHONE_ERROR_MESSAGE}</Alert>}
        
        <Button type="submit">Salvar alterações</Button>
      </ContactFormHeaderScroll>
    </Form>
  );
};
